/**
 * FederatedEditor - configure a cross-source (DuckDB-federated) widget.
 *
 * Each subquery targets one live dataset. When the dataset has a governed semantic model
 * we show measure/dimension pickers ("Governed" mode); otherwise (or when the user opts
 * out) we fall back to a raw QuerySpec JSON textarea ("Advanced" mode).
 *
 * Both modes serialise into the backend subquery contract:
 *   Governed → { alias, dataset_id, semantic_query: { dimensions:[{key}], measures:[key] } }
 *   Advanced → { alias, dataset_id, spec: { dimensions:[...], measures:[...] } }
 */
import React, { useEffect, useState } from "react";
import { Plus, Trash2, Play, CheckCircle2, AlertCircle, X, Sparkles, Wrench, Link2, Bookmark } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AIFederatedDialog } from "@/components/bi/AIFederatedDialog";
import { KeyPairsEditor } from "@/components/bi/KeyPairsEditor";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY_SPEC = `{
  "dimensions": [{"field": "region"}],
  "measures":   [{"field": "amount", "agg": "sum"}]
}`;

const DEFAULT_COMBINE = `SELECT crm.region,
       crm.amount AS crm_pipeline,
       erp.sales  AS erp_revenue
FROM crm
LEFT JOIN erp USING (region)
ORDER BY erp_revenue DESC NULLS LAST`;

// Simple in-memory cache of the semantic model per dataset so switching between subqueries
// doesn't refetch every time.
const modelCache = new Map();
async function loadModel(datasetId) {
  if (!datasetId) return null;
  if (modelCache.has(datasetId)) return modelCache.get(datasetId);
  try {
    const { data } = await api.get(`/datasets/${datasetId}/model`);
    modelCache.set(datasetId, data);
    return data;
  } catch (e) {
    return null;
  }
}

export function FederatedEditor({ datasets = [], value, onChange, onMapColumns = null, dashboardFilters = [] }) {
  const spec = value || { subqueries: [], combine_sql: "" };
  const [status, setStatus] = useState(null); // { ok, message, columns }
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [saveContractOpen, setSaveContractOpen] = useState(false);

  const dsIds = (spec.subqueries || []).map((s) => s.dataset_id).filter(Boolean);
  const dsIdsKey = JSON.stringify([...dsIds].sort());
  // Fetch join contracts that match any pair of picked datasets
  useEffect(() => {
    if (dsIds.length < 2) { setContracts([]); return; }
    let cancelled = false;
    (async () => {
      const seen = new Set(), out = [];
      for (let i = 0; i < dsIds.length; i++) {
        for (let j = i + 1; j < dsIds.length; j++) {
          try {
            const { data } = await api.get(`/join-contracts?between=${dsIds[i]},${dsIds[j]}`);
            (data || []).forEach((c) => { if (!seen.has(c.id)) { seen.add(c.id); out.push(c); } });
          } catch (e) { /* ignore */ }
        }
      }
      if (!cancelled) setContracts(out);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsIdsKey]);

  const patch = (p) => onChange({ ...spec, ...p });
  const setSub = (i, patchObj) => {
    const arr = [...(spec.subqueries || [])];
    arr[i] = { ...arr[i], ...patchObj };
    patch({ subqueries: arr });
  };

  const addSub = () => {
    const idx = (spec.subqueries || []).length;
    const alias = idx === 0 ? "crm" : idx === 1 ? "erp" : `t${idx + 1}`;
    patch({
      subqueries: [
        ...(spec.subqueries || []),
        { alias, dataset_id: "", mode: "governed", measures: [], dimensions: [], spec_json: EMPTY_SPEC },
      ],
      combine_sql: spec.combine_sql || DEFAULT_COMBINE,
    });
  };

  const removeSub = (i) => {
    const arr = [...(spec.subqueries || [])];
    arr.splice(i, 1);
    patch({ subqueries: arr });
  };

  // Build the payload the backend expects from the editor state
  const buildPayload = () => {
    const subqueries = (spec.subqueries || []).map((s) => {
      const base = { alias: s.alias, dataset_id: s.dataset_id };
      if (s.mode === "advanced") {
        let parsed;
        try { parsed = JSON.parse(s.spec_json || "{}"); }
        catch (e) { throw new Error(`[${s.alias}] Invalid JSON`); }
        return { ...base, spec: parsed };
      }
      // Governed
      return {
        ...base,
        semantic_query: {
          dimensions: (s.dimensions || []).map((k) => ({ key: k })),
          measures: s.measures || [],
        },
      };
    });
    return { subqueries, combine_sql: spec.combine_sql || "" };
  };

  // Pull a few real rows so numeric columns land in the measure well, text in the dimension well.
  const useColumns = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/federated/query", { ...buildPayload(), limit: 5 });
      onMapColumns(data.columns || [], data.rows || []);
      toast.success("Columns mapped onto the chart");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the result columns");
    } finally { setBusy(false); }
  };

  // Insert a filter token so the SQL stays valid: wrap the query once, then AND into it.
  const insertToken = (f) => {
    const cur = (spec.combine_sql || "").trim();
    const wrapped = /\)\s*t\b/i.test(cur);
    const expr = `${wrapped || !cur ? "t." : ""}${f.field || "column"}`;
    const token = `{{where:${f.id}:${expr}}}`;
    if (!cur) {
      patch({ combine_sql: `-- write the combine SQL first, then filter it:\n-- SELECT * FROM (…) t WHERE ${token}` });
      return;
    }
    if (wrapped && /\bWHERE\b/i.test(cur)) {
      patch({ combine_sql: `${cur}\n  AND ${token}` });
      return;
    }
    patch({ combine_sql: `SELECT * FROM (\n${cur}\n) t\nWHERE ${token}` });
  };

  const validate = async () => {
    setBusy(true); setStatus(null);
    try {
      const payload = buildPayload();
      const { data } = await api.post("/federated/validate", payload);
      setStatus({ ok: true, message: `Ok. ${data.columns?.length || 0} columns.`, columns: data.columns || [] });
    } catch (e) {
      setStatus({ ok: false, message: e?.response?.data?.detail || e.message || "Validation failed" });
    } finally { setBusy(false); }
  };

  // Apply a saved join contract: figure out which subquery is 'left' / 'right' and
  // synthesise a combine_sql from the governed measures picked in each subquery.
  const applyContract = (contract) => {
    const subs = spec.subqueries || [];
    const left = subs.find((s) => s.dataset_id === contract.left_dataset_id);
    const right = subs.find((s) => s.dataset_id === contract.right_dataset_id);
    if (!left || !right) {
      toast.error("Contract needs one subquery on each side. Add both datasets first.");
      return;
    }
    // Prefer governed keys as output columns
    const leftMeas = (left.measures && left.measures.length ? left.measures : []).slice(0, 3);
    const rightMeas = (right.measures && right.measures.length ? right.measures : []).slice(0, 3);
    // USING requires identical column names — else fall back to ON clause
    const identical = contract.keys.every((k) => k.left_field === k.right_field);
    const joinCols = contract.keys.map((k) => k.left_field);
    const on = identical
      ? `USING (${joinCols.join(", ")})`
      : "ON " + contract.keys.map((k) => `${left.alias}.${k.left_field} = ${right.alias}.${k.right_field}`).join(" AND ");
    const selectCols = [
      ...joinCols.map((c) => `${left.alias}.${c}`),
      ...leftMeas.map((m) => `${left.alias}.${m} AS ${left.alias}_${m}`),
      ...rightMeas.map((m) => `${right.alias}.${m} AS ${right.alias}_${m}`),
    ];
    const sql = `SELECT ${selectCols.join(",\n       ")}\nFROM ${left.alias}\nLEFT JOIN ${right.alias} ${on}`;
    patch({ combine_sql: sql });
    toast.success(`Combine SQL generated from "${contract.name}"`);
  };

  const applyAIPlan = (plan) => {
    // Convert AI plan → editor state (mode + measures + dimensions per subquery)
    const newSubs = (plan.subqueries || []).map((s, i) => {
      const sq = s.semantic_query || {};
      return {
        alias: s.alias || `t${i + 1}`,
        dataset_id: s.dataset_id,
        mode: "governed",
        measures: sq.measures || [],
        dimensions: (sq.dimensions || []).map((d) => d.key).filter(Boolean),
        spec_json: EMPTY_SPEC,
      };
    });
    onChange({ subqueries: newSubs, combine_sql: plan.combine_sql || "" });
    toast.success("AI plan applied. Review + validate before saving.");
  };

  return (
    <div className="space-y-3 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex flex-wrap items-start justify-between gap-1.5">
        <div className="min-w-[120px] flex-1">
          <Label className="text-xs font-semibold text-[color:var(--ink)]">Cross-source (Federated)</Label>
          <p className="text-[10px] text-[color:var(--ink-3)] leading-tight">
            Each subquery runs on its own source; the results are joined here.
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button data-testid="ai-fed-open" size="sm" variant="outline" onClick={() => setAiOpen(true)} className="h-7 border-[color:var(--brand-mint)] text-[color:var(--brand-mint)] hover:bg-[color:rgba(0,194,168,0.10)]">
            <Sparkles className="w-3.5 h-3.5 mr-1" />AI Assist
          </Button>
          <Button data-testid="add-subquery" size="sm" variant="outline" onClick={addSub} className="h-7">
            <Plus className="w-3.5 h-3.5 mr-1" />Subquery
          </Button>
        </div>
      </div>

      {contracts.length > 0 && (
        <div data-testid="contracts-bar" className="rounded-md bg-[color:rgba(43,78,255,0.06)] border border-[color:var(--brand-ocean)]/25 p-2 space-y-1.5">
          <div className="flex items-center gap-1.5 text-[10px] text-[color:var(--brand-ocean)]">
            <Link2 className="w-3 h-3" />
            <span className="font-medium">{contracts.length} join contract{contracts.length > 1 ? "s" : ""} available</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {contracts.map((c) => (
              <button
                key={c.id}
                data-testid={`contract-apply-${c.id}`}
                onClick={() => applyContract(c)}
                className="text-[11px] px-2 py-0.5 rounded-full bg-[color:var(--surface)] border border-[color:var(--brand-ocean)]/40 text-[color:var(--brand-ocean)] hover:bg-[color:rgba(43,78,255,0.10)]"
                title={"Apply: " + c.keys.map((k) => `${k.left_field}=${k.right_field}`).join(", ")}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {(spec.subqueries || []).length === 0 && (
        <div className="text-[11px] text-[color:var(--ink-3)] italic px-1">
          No subqueries yet. Add at least two (e.g. crm + erp) then write a combine SQL.
        </div>
      )}

      {(spec.subqueries || []).map((s, i) => (
        <SubqueryRow
          key={i}
          index={i}
          sub={s}
          datasets={datasets}
          onChange={(p) => setSub(i, p)}
          onRemove={() => removeSub(i)}
        />
      ))}

      <div>
        <Label className="text-xs font-semibold text-[color:var(--ink-2)]">Combine SQL (DuckDB)</Label>
        <Textarea
          data-testid="combine-sql"
          value={spec.combine_sql || ""}
          onChange={(e) => patch({ combine_sql: e.target.value })}
          className="mt-1 h-32 font-mono text-[11px] bg-[color:var(--surface)]"
          placeholder={DEFAULT_COMBINE}
          spellCheck={false}
        />
        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">
          Reference each subquery by its alias. Column names in a governed subquery are the measure/dimension <em>keys</em> (e.g. <code>crm.amount</code>). Only SELECT / WITH statements allowed.
        </p>
        {dashboardFilters.length > 0 && (
          <div data-testid="fed-filter-tokens" className="mt-1.5 rounded-lg bg-[color:var(--surface-2)] p-2">
            <p className="text-[10px] text-[color:var(--ink-3)] leading-snug">
              Filter tokens: click one to filter the combined result by that dashboard filter. It becomes a real
              predicate at run time (or <code>TRUE</code> when the viewer hasn’t picked anything).
            </p>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {dashboardFilters.map((f) => (
                <button key={f.id} data-testid={`fed-token-${f.id}`} type="button"
                  title={`Insert a predicate driven by the "${f.label || f.field}" filter`}
                  onClick={() => insertToken(f)}
                  className="text-[10px] font-mono rounded-full px-2 py-0.5 bg-[color:var(--surface)] border border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:border-[color:var(--brand-ocean)] hover:text-[color:var(--brand-ocean)]">
                  {f.label || f.field || f.id}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button data-testid="fed-validate" size="sm" onClick={validate} disabled={busy} className="h-8">
          <Play className="w-3.5 h-3.5 mr-1" />{busy ? "Running…" : "Validate"}
        </Button>
        {dsIds.length >= 2 && (
          <Button data-testid="save-contract-open" size="sm" variant="outline" onClick={() => setSaveContractOpen(true)} className="h-8">
            <Bookmark className="w-3.5 h-3.5 mr-1" />Save as contract
          </Button>
        )}
        {onMapColumns && status?.ok && status.columns?.length > 0 && (
          <Button data-testid="fed-map-columns" size="sm" variant="outline" onClick={useColumns}
            className="h-8 border-[color:var(--brand-mint)] text-[color:var(--brand-mint)] hover:bg-[color:rgba(0,194,168,0.10)]">
            Use these columns
          </Button>
        )}
        {status && (
          <div data-testid="fed-status" className={"text-[11px] flex items-center gap-1 " + (status.ok ? "text-[color:var(--success)]" : "text-[color:var(--danger)]")}>
            {status.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
            {status.message}
          </div>
        )}
      </div>

      <AIFederatedDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        datasets={datasets}
        onApply={applyAIPlan}
      />
      <SaveContractDialog
        open={saveContractOpen}
        onOpenChange={setSaveContractOpen}
        subqueries={spec.subqueries || []}
        datasets={datasets}
        onSaved={(c) => { setContracts([c, ...contracts]); toast.success(`Contract "${c.name}" saved`); }}
      />
    </div>
  );
}

function SaveContractDialog({ open, onOpenChange, subqueries, datasets, onSaved }) {
  const [leftIdx, setLeftIdx] = useState(0);
  const [rightIdx, setRightIdx] = useState(1);
  const [name, setName] = useState("");
  const [keys, setKeys] = useState([{ left_field: "", right_field: "" }]);
  const [saving, setSaving] = useState(false);

  const left = subqueries[leftIdx];
  const right = subqueries[rightIdx];
  const leftDs = datasets.find((d) => d.id === left?.dataset_id);
  const rightDs = datasets.find((d) => d.id === right?.dataset_id);
  const leftCols = (leftDs?.schema || []).map((c) => c.name);
  const rightCols = (rightDs?.schema || []).map((c) => c.name);

  const save = async () => {
    const validKeys = keys.filter((k) => k.left_field && k.right_field);
    if (!left?.dataset_id || !right?.dataset_id || validKeys.length === 0 || !name.trim()) {
      toast.error("Fill name, both datasets and at least one complete key pair");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/join-contracts", {
        name: name.trim(),
        left_dataset_id: left.dataset_id,
        right_dataset_id: right.dataset_id,
        keys: validKeys,
      });
      onSaved(data);
      onOpenChange(false);
      setName(""); setKeys([{ left_field: "", right_field: "" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-[color:var(--brand-ocean)]" />
            Save join contract
          </DialogTitle>
          <DialogDescription>
            Declare how these two datasets should be joined. Composite keys are supported.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input data-testid="contract-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="e.g. CRM leads ↔ ERP orders" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Left subquery</Label>
              <Select value={String(leftIdx)} onValueChange={(v) => setLeftIdx(Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {subqueries.map((s, i) => <SelectItem key={i} value={String(i)}>{s.alias || `subquery ${i + 1}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Right subquery</Label>
              <Select value={String(rightIdx)} onValueChange={(v) => setRightIdx(Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {subqueries.map((s, i) => <SelectItem key={i} value={String(i)}>{s.alias || `subquery ${i + 1}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <KeyPairsEditor
              value={keys}
              onChange={setKeys}
              leftColumns={leftCols}
              rightColumns={rightCols}
              leftLabel="Left.field"
              rightLabel="Right.field"
              testIdPrefix="contract"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="contract-save" onClick={save} disabled={saving}>Save contract</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubqueryRow({ index, sub, datasets, onChange, onRemove }) {
  const [model, setModel] = useState(null);
  const [loadingModel, setLoadingModel] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!sub.dataset_id) { setModel(null); return; }
    setLoadingModel(true);
    loadModel(sub.dataset_id).then((m) => {
      if (!cancelled) { setModel(m); setLoadingModel(false); }
    });
    return () => { cancelled = true; };
  }, [sub.dataset_id]);

  const measures = (model?.measures || []).filter((m) => m.visible !== false);
  const dims = (model?.dimensions || []).filter((d) => d.visible !== false);
  const hasGoverned = measures.length > 0;
  const mode = sub.mode || (hasGoverned ? "governed" : "advanced");

  const toggleMeasure = (key) => {
    const cur = new Set(sub.measures || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    onChange({ measures: [...cur] });
  };
  const toggleDim = (key) => {
    const cur = new Set(sub.dimensions || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    onChange({ dimensions: [...cur] });
  };

  return (
    <div data-testid={`subquery-row-${index}`} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2.5 space-y-2">
      <div className="flex gap-1.5 items-center">
        <Input
          data-testid={`sub-alias-${index}`}
          value={sub.alias || ""}
          onChange={(e) => onChange({ alias: e.target.value.replace(/[^a-zA-Z0-9_]/g, "") })}
          placeholder="alias (e.g. crm)"
          className="h-8 flex-1 font-mono text-xs"
        />
        <Select value={sub.dataset_id || ""} onValueChange={(v) => onChange({ dataset_id: v, measures: [], dimensions: [] })}>
          <SelectTrigger data-testid={`sub-dataset-${index}`} className="h-8 flex-1 text-xs"><SelectValue placeholder="Dataset" /></SelectTrigger>
          <SelectContent>
            {datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid={`sub-remove-${index}`} size="icon" variant="ghost" onClick={onRemove} className="h-8 w-8 text-[color:var(--danger)]">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {sub.dataset_id && hasGoverned && (
        <div className="flex items-center justify-between rounded-md bg-[color:var(--surface-2)] px-2 py-1.5">
          <div className="flex items-center gap-1.5 text-[10px] text-[color:var(--ink-3)]">
            {mode === "governed" ? <Sparkles className="w-3 h-3 text-[color:var(--brand-mint)]" /> : <Wrench className="w-3 h-3 text-[color:var(--brand-ocean)]" />}
            <span className="text-[color:var(--ink-2)] font-medium">{mode === "governed" ? "Governed" : "Advanced"}</span>
            <span className="opacity-70">·</span>
            <span className="opacity-70">{mode === "governed" ? "pick measures & dimensions" : "raw QuerySpec JSON"}</span>
          </div>
          <Switch
            data-testid={`sub-mode-toggle-${index}`}
            checked={mode === "advanced"}
            onCheckedChange={(v) => onChange({ mode: v ? "advanced" : "governed" })}
          />
        </div>
      )}

      {sub.dataset_id && mode === "governed" && !loadingModel && (
        hasGoverned ? (
          <div className="space-y-2">
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Measures</Label>
              <div data-testid={`sub-measures-${index}`} className="flex flex-wrap gap-1 mt-1">
                {measures.map((m) => {
                  const on = (sub.measures || []).includes(m.key);
                  return (
                    <button
                      key={m.key}
                      onClick={() => toggleMeasure(m.key)}
                      className={"text-[11px] px-2 py-0.5 rounded-full border transition-colors " +
                        (on ? "bg-[color:rgba(0,194,168,0.15)] border-[color:var(--success)] text-[color:var(--success)]"
                            : "bg-[color:var(--surface)] border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]")}
                    >
                      {m.label || m.key}
                      {on && <X className="w-2.5 h-2.5 inline ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-[color:var(--ink-3)]">Group by (dimensions)</Label>
              <div data-testid={`sub-dimensions-${index}`} className="flex flex-wrap gap-1 mt-1">
                {dims.map((d) => {
                  const on = (sub.dimensions || []).includes(d.key);
                  return (
                    <button
                      key={d.key}
                      onClick={() => toggleDim(d.key)}
                      className={"text-[11px] px-2 py-0.5 rounded-full border transition-colors " +
                        (on ? "bg-[color:rgba(43,78,255,0.12)] border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)]"
                            : "bg-[color:var(--surface)] border-[color:var(--glass-border)] text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)]")}
                    >
                      {d.label || d.key}
                      {on && <X className="w-2.5 h-2.5 inline ml-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <p className="text-[10px] text-[color:var(--ink-3)] italic">
              Output column names will be the picked keys (use e.g. <code>{sub.alias}.{sub.measures?.[0] || "measure_key"}</code> in the combine SQL).
            </p>
          </div>
        ) : (
          <p className="text-[10px] text-[color:var(--ink-3)] italic">
            No governed model yet for this dataset. Switch to Advanced or set up measures in Modeling Studio.
          </p>
        )
      )}

      {sub.dataset_id && mode === "advanced" && (
        <div>
          <Label className="text-[10px] text-[color:var(--ink-3)]">Subquery spec (JSON)</Label>
          <Textarea
            data-testid={`sub-spec-${index}`}
            value={sub.spec_json || EMPTY_SPEC}
            onChange={(e) => onChange({ spec_json: e.target.value })}
            className="mt-1 h-24 font-mono text-[11px] bg-[color:var(--surface-2)]"
            spellCheck={false}
          />
          <p className="text-[10px] text-[color:var(--ink-3)] mt-1 italic">
            Output columns: <code>{sub.alias}.&lt;dim_field&gt;</code>, <code>{sub.alias}.&lt;agg&gt;_&lt;measure_field&gt;</code> (e.g. <code>{sub.alias}.sum_amount</code>).
          </p>
        </div>
      )}
    </div>
  );
}
